import { useEffect, useRef, useState } from 'react'
import type { ClienteDTO, PagaebleResult, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { firstFilled, fiscalLabel, initials, pageWindow, personName } from '../common/customer-common'

export const PAGE_SIZES = [20, 50, 100]
const MIN_SEARCH_LENGTH = 3
const SEARCH_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de clientes.'

export interface CustomerRow {
    id: number
    name: string
    initials: string
    rfc: string
    email: string
    phone: string
    fiscal: string
    esRobot: boolean
}

const toRow = (cliente: ClienteDTO): CustomerRow => {
    const name = personName(cliente)
    return {
        id: cliente.id,
        name,
        initials: initials(name),
        rfc: String(cliente.rfc || '').trim(),
        email: firstFilled(cliente.correos?.map((item) => item?.correo) || []),
        phone: firstFilled(cliente.celulares?.map((item) => item?.numero) || []),
        fiscal: fiscalLabel(cliente.fiscal),
        esRobot: Boolean(cliente.esRobot),
    }
}

const isSearchable = (value: string) => value.length === 0 || value.length >= MIN_SEARCH_LENGTH

export const useCustomerListModel = () => {
    const history = useHistory()
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [nombre, setNombre] = useState('')
    const [queryNombre, setQueryNombre] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<ClienteDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(null)
    const [bitacoraId, setBitacoraId] = useState<number | null>(null)
    const [adjuntosId, setAdjuntosId] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const nombreParam = queryNombre.length >= MIN_SEARCH_LENGTH ? `&nombre=${encodeURIComponent(queryNombre)}` : ''
                const response = await APIInvoke.invokeGET(`/clientes?page=${page}&size=${pageSize}${nombreParam}&order=nombres asc`)
                const payload: WrapperResponse<PagaebleResult<ClienteDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError((loadError instanceof Error && loadError.message) || LOAD_ERROR)
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, queryNombre, reloadToken])

    useEffect(() => () => clearTimeout(textTimer.current), [])

    const applySearch = (value: string) => {
        const trimmed = value.trim()
        if (!isSearchable(trimmed)) return
        setPage(1)
        setQueryNombre(trimmed)
    }

    const changeSearch = (value: string) => {
        setNombre(value)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => applySearch(value), SEARCH_DELAY_MS)
    }

    const submitSearch = () => {
        clearTimeout(textTimer.current)
        applySearch(nombre)
    }

    const clearSearch = () => {
        setNombre('')
        clearTimeout(textTimer.current)
        setPage(1)
        setQueryNombre('')
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const reload = () => setReloadToken((token) => token + 1)
    const openCliente = (id: number) => setDetailId(id)
    const closeCliente = () => setDetailId(null)
    const viewCliente = (id: number) => history.push(`/clientes/${id}`)
    const editCliente = (id: number) => history.push({ pathname: `/clientes/${id}/edit`, state: { backUrl: '/clientes' } })

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        nombre,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        searchHint: !isSearchable(nombre.trim()),
        detailId,
        bitacoraId,
        adjuntosId,
        changeSearch,
        submitSearch,
        clearSearch,
        setPage,
        changePageSize,
        reload,
        openCliente,
        closeCliente,
        viewCliente,
        editCliente,
        closeBitacora: () => setBitacoraId(null),
        closeAdjuntos: () => setAdjuntosId(null),
    }
}
